import { 
    NormalizedChildDocumentTemplateViewItem,
    NormalizedDocumentTemplateViewItem,
} from './model'
import { AddProjectContentItemInput } from '@deltek/specification-client/core/Models/projectProductProperty'
import { TextContentElementInput } from '@deltek/specification-client/core/Models/textContentElement'
import {
    getUniqueKey,
    getParentKey,
    getActualKeyWithParent,
    getContentOwnerId,
    getContentOwnerIdforNewProperty,
    getContentOwnerType
} from './utils'
import {
    getNewlyAddedProperties,
    getPropertyDefaultValue,
    articleDefaultValue,
    defaultNewProperty,
    isSustainabilityTopicContent,
    isSustainabilityTopicContentInNuggetorPTP
} from './addUtils'

export const createDefaultProperties = (
    newValue: string,
    projectId: string,
    projectProductFamilyId?: string
) => ({
    projectId: projectId,
    projectProductFamilyId: projectProductFamilyId ?? '',
    elements: [
        {
            text: newValue || '',
        } as TextContentElementInput,
    ],
})

export const createNewPropertyStructure = (
    hasTitle: boolean,
    temporaryParagraphText?: string
): Partial<NormalizedChildDocumentTemplateViewItem> => ({
    ...defaultNewProperty,
    contentItem: {
        ...defaultNewProperty.contentItem,
        elements: [
            {
                ...defaultNewProperty.contentItem?.elements?.[0],
                text: hasTitle
                    ? articleDefaultValue
                    : temporaryParagraphText || getPropertyDefaultValue(),
            },
        ],
    },
})

export const calculateParentKey = (
    parent?: NormalizedDocumentTemplateViewItem
): string => {
    if (!parent) return ''
    
    if (parent.isUserAdded && parent.level > 1 && !parent.parentKey) {
        const parentElementText = parent.contentItem?.elements?.[0]?.text
        return parentElementText ? getParentKey(parentElementText) : ''
    }
    
    return parent.parentKey ? getParentKey(parent.parentKey) : ''
}

export const calculateLevelAndOrder = (
    property: NormalizedDocumentTemplateViewItem,
    parent?: NormalizedDocumentTemplateViewItem
) => {
    const currentLevel = 
        isSustainabilityTopicContent(property) && property?.level === 1
            ? 0
            : parent?.level
    
    const level = (currentLevel as number) + 1
    
    const order = !!isSustainabilityTopicContentInNuggetorPTP(property)
        ? (property?.order as number) + property?.children?.length + 1
        : (property?.order as number) + 1
    
    const paragraphOrder = 
        property?.type === 'Table' || property?.isTableSibling
            ? (property?.order as number) + 1
            : isSustainabilityTopicContentInNuggetorPTP(property)
            ? order + 1
            : (property?.level as number) <= 2
            ? (property?.paragraphOrder as number) + 1
            : undefined

    return { level, order, paragraphOrder }
}

export const setupParagraphProperty = (
    newProperty: Partial<NormalizedChildDocumentTemplateViewItem>,
    property: NormalizedDocumentTemplateViewItem,
    parent: NormalizedDocumentTemplateViewItem,
    isPropertyShared: boolean
) => {
    newProperty.newProperties = getNewlyAddedProperties(
        parent,
        isPropertyShared,
        property
    )
    
    const { level, order, paragraphOrder } = calculateLevelAndOrder(property, parent)
    newProperty.level = level
    newProperty.order = order
    newProperty.paragraphOrder = paragraphOrder
    newProperty.addingType = 'keyless'
}

export const setupArticleProperty = (
    newProperty: Partial<NormalizedChildDocumentTemplateViewItem>
) => {
    newProperty.contentItem = {
        ...newProperty.contentItem,
        textType: 'Article',
    }
}

export const getContentOwnerTypeNew = (
    parent: NormalizedDocumentTemplateViewItem,
    template: NormalizedDocumentTemplateViewItem,
    newProperties: any
) => {
    let contentItemOwnerType = newProperties?.contentItemOwnerType
    let isPTP = template?.parentType === 'ProductTypeProperty'

    if (parent?.documentTemplateViewContents) {
        parent?.documentTemplateViewContents.forEach((content) => {
            if (content?.documentTemplateViewContentItems) {
                const documentTemplateViewContentItems = content?.documentTemplateViewContentItems.find(
                    (contentItem) =>
                        contentItem?.id === newProperties?.contentItemParentId
                )
                if (!documentTemplateViewContentItems?.parentId) {
                    contentItemOwnerType = !!isPTP
                        ? template?.parentType
                        : parent?.type
                }
            }
        })
    }

    return contentItemOwnerType
}

export const createPropertyInput = (
    newValue: string,
    property: NormalizedDocumentTemplateViewItem,
    parent: NormalizedDocumentTemplateViewItem,
    template: NormalizedDocumentTemplateViewItem,
    newProperties: any,
    selectedTopic: any,
    projectId: string,
    projectProductFamilyId?: string,
    useNewContentOwnerType?: boolean
) => {
    const uniqueKey = getUniqueKey(newValue)
    const parentKey = calculateParentKey(parent)
    const actualKeyWithParent = getActualKeyWithParent(uniqueKey as string, parentKey)

    const contentItemParentType = selectedTopic
        ? 'None'
        : (useNewContentOwnerType 
            ? newProperties.newProperties.contentItemParentType
            : template?.newProperties?.contentItemParentType) === 'ContentItem'
        ? (useNewContentOwnerType 
            ? newProperties.newProperties.contentItemParentType
            : template?.newProperties?.contentItemParentType)
        : parent?.parentType

    const contentItemOwnerType = useNewContentOwnerType
        ? getContentOwnerTypeNew(
            parent as NormalizedChildDocumentTemplateViewItem,
            template as NormalizedDocumentTemplateViewItem,
            newProperties.newProperties
          )
        : getContentOwnerType(
            parent as NormalizedChildDocumentTemplateViewItem,
            template as NormalizedDocumentTemplateViewItem
          )

    const contentItemOwnerId = getContentOwnerIdforNewProperty(
        uniqueKey as string,
        parent as NormalizedChildDocumentTemplateViewItem,
        template as NormalizedDocumentTemplateViewItem
    )

    const defaultProperties = createDefaultProperties(newValue, projectId, projectProductFamilyId)
    const propertiesToUse = useNewContentOwnerType ? newProperties.newProperties : property.newProperties

    return {
        ...propertiesToUse,
        ...defaultProperties,
        index: template?.order,
        contentItemOwnerType,
        contentItemParentType,
        contentItemOwnerId,
        propertyKey: actualKeyWithParent,
    }
}

export const createArticleInput = (
    newValue: string,
    property: NormalizedDocumentTemplateViewItem,
    parent: NormalizedDocumentTemplateViewItem,
    projectId: string,
    projectProductFamilyId?: string
): Partial<AddProjectContentItemInput> => {
    const isPTGroupArticleNugget = parent?.partType == 'Part' && parent.isProduct
    const defaultProperties = createDefaultProperties(newValue, projectId, projectProductFamilyId)

    const input: Partial<AddProjectContentItemInput> = {
        ...defaultProperties,
        contentItemOwnerType: 'Nugget',
        contentItemParentId: property.parentId as string,
        contentItemParentType: 'None',
        index: property?.order as number,
        partType: property.partType,
        textType: 'Article',
        subElements: [{ text: 'Insert your Paragraph.' }],
        productTypePropertyType: undefined,
    }

    if (!isPTGroupArticleNugget) {
        input.contentItemOwnerType = 'ProductTypeProperty'
        input.propertyKey = newValue
        input.productTypePropertyType = 'Shared'
    }

    return input
}

export const createPillWithNewParaInput = (
    newValue: string,
    property: NormalizedDocumentTemplateViewItem,
    parent: NormalizedDocumentTemplateViewItem,
    projectId: string,
    projectProductFamilyId?: string
) => {
    const uniqueKey = getUniqueKey(newValue)
    const parentKey = getParentKey(parent?.parentKey as string)
    const actualKeyWithParent = getActualKeyWithParent(uniqueKey as string, parentKey)
    const defaultProperties = createDefaultProperties(newValue, projectId, projectProductFamilyId)

    return {
        ...property.newProperties,
        ...defaultProperties,
        contentItemOwnerId: !uniqueKey
            ? parent?.type === 'ContentItem'
                ? getContentOwnerId(parent as NormalizedChildDocumentTemplateViewItem)
                : parent?.id
            : undefined,
        propertyKey: actualKeyWithParent,
    }
}